import { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Activity,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Shield,
  Home,
  FileText,
  LayoutTemplate,
  Workflow,
  Link2,
  DollarSign,
} from "lucide-react";

const navItems = [
  { icon: LayoutDashboard, label: "Overview", path: "/admin" },
  { icon: Users, label: "Users", path: "/admin/users" },
  { icon: CreditCard, label: "Credits & Billing", path: "/admin/billing" },
  { icon: DollarSign, label: "AI Costs", path: "/admin/ai-costs" },
  { icon: LayoutTemplate, label: "Templates", path: "/admin/templates" },
  { icon: Activity, label: "Activity Logs", path: "/admin/activity" },
  { icon: FileText, label: "Prompts", path: "/admin/prompts" },
  { icon: Workflow, label: "Pipeline Builder", path: "/admin/pipeline-builder" },
  { icon: Link2, label: "Link Tracking", path: "/admin/link-tracking" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

export default function AdminLayout() {
  const { user, isAdmin, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      navigate("/auth");
      return;
    }

    // Only redirect if isAdmin is explicitly false (not undefined/null)
    // This prevents race conditions where roles haven't loaded yet
    if (isAdmin === false) {
      toast({
        title: "Access Denied",
        description: "You don't have admin privileges",
        variant: "destructive",
      });
      navigate("/");
      return;
    }
  }, [user, isAdmin, authLoading, navigate, toast]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user || isAdmin !== true) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-background-secondary via-background to-background-tertiary">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-0 z-40 h-screen border-r border-primary/10 bg-gradient-to-b from-primary/5 via-card to-card shadow-lg transition-all duration-300",
          collapsed ? "w-16" : "w-64"
        )}
      >
        {/* Logo/Title */}
        <div className="flex h-16 items-center justify-between border-b border-primary/10 px-4 bg-gradient-to-r from-primary/10 to-accent/5">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary shadow-primary">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="font-bold text-lg bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">Admin</span>
            </div>
          )}
          {collapsed && (
            <div className="p-1.5 rounded-lg bg-primary shadow-primary mx-auto">
              <Shield className="h-5 w-5 text-primary-foreground" />
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className={cn("h-8 w-8 hover:bg-primary/10", collapsed && "mx-auto")}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex flex-col gap-1.5 p-3">
          {navItems.map((item, index) => {
            const isActive =
              item.path === "/admin"
                ? location.pathname === "/admin"
                : location.pathname.startsWith(item.path);

            const colors = [
              { bg: "bg-info/10", text: "text-info", activeBg: "bg-info", hoverBg: "hover:bg-info/10" },
              { bg: "bg-success/10", text: "text-success", activeBg: "bg-success", hoverBg: "hover:bg-success/10" },
              { bg: "bg-warning/10", text: "text-warning", activeBg: "bg-warning", hoverBg: "hover:bg-warning/10" },
              { bg: "bg-accent/10", text: "text-accent", activeBg: "bg-accent", hoverBg: "hover:bg-accent/10" },
              { bg: "bg-primary/10", text: "text-primary", activeBg: "bg-primary", hoverBg: "hover:bg-primary/10" },
            ];
            const color = colors[index % colors.length];

            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200",
                  isActive
                    ? `${color.activeBg} text-white shadow-md`
                    : `text-muted-foreground ${color.hoverBg} hover:text-foreground`
                )}
              >
                <div className={cn(
                  "p-1.5 rounded-lg transition-colors",
                  isActive ? "bg-white/20" : color.bg
                )}>
                  <item.icon className={cn("h-4 w-4 flex-shrink-0", isActive ? "text-white" : color.text)} />
                </div>
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-primary/10 p-3 space-y-1.5 bg-gradient-to-t from-muted/30 to-transparent">
          <Link
            to="/"
            className={cn(
              "flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-linkedin/10 hover:text-linkedin transition-all duration-200"
            )}
          >
            <div className="p-1.5 rounded-lg bg-linkedin/10">
              <Home className="h-4 w-4 flex-shrink-0 text-linkedin" />
            </div>
            {!collapsed && <span>Back to App</span>}
          </Link>
          <button
            onClick={handleSignOut}
            className={cn(
              "flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-all duration-200"
            )}
          >
            <div className="p-1.5 rounded-lg bg-destructive/10">
              <LogOut className="h-4 w-4 flex-shrink-0 text-destructive" />
            </div>
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main
        className={cn(
          "flex-1 transition-all duration-300",
          collapsed ? "ml-16" : "ml-64"
        )}
      >
        <div className="p-6 min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
